import React, { useEffect, useState, useCallback } from 'react';
import {
  Card, Table, Tag, Space, Button, Input, Select, Modal, Form, message,
  Typography, Avatar, Popconfirm, Drawer, Descriptions, Badge,
} from 'antd';
import {
  SearchOutlined, UserOutlined, EditOutlined, StopOutlined,
  CheckCircleOutlined, EyeOutlined, ReloadOutlined,
} from '@ant-design/icons';
import { adminUsersAPI } from '../services/api';
import { useLanguage } from '../store/LanguageContext';
import type { User } from '../types';

const { Title, Text } = Typography;

const AdminUserManagementPage: React.FC = () => {
  const { t } = useLanguage();
  const [loading, setLoading] = useState(false);
  const [users, setUsers] = useState<User[]>([]);
  const [pagination, setPagination] = useState({ page: 1, limit: 20, total: 0 });
  const [search, setSearch] = useState('');
  const [roleFilter, setRoleFilter] = useState<string | undefined>();
  const [activeFilter, setActiveFilter] = useState<string | undefined>();
  const [editModalVisible, setEditModalVisible] = useState(false);
  const [detailDrawer, setDetailDrawer] = useState<{ visible: boolean; user?: User; stats?: any }>({ visible: false });
  const [editingUser, setEditingUser] = useState<User | null>(null);
  const [form] = Form.useForm();

  const fetchUsers = useCallback(async (page = 1) => {
    setLoading(true);
    try {
      const params: any = { page, limit: pagination.limit };
      if (search) params.search = search;
      if (roleFilter) params.role = roleFilter;
      if (activeFilter !== undefined) params.is_active = activeFilter;
      const res = await adminUsersAPI.getUsers(params);
      setUsers(res.data.users);
      setPagination(prev => ({
        ...prev,
        page: res.data.pagination.page,
        total: res.data.pagination.total,
      }));
    } catch {
      message.error(t('adminPanel.fetchFailed'));
    } finally {
      setLoading(false);
    }
  }, [search, roleFilter, activeFilter, pagination.limit, t]);

  useEffect(() => {
    fetchUsers();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const handleSearch = () => fetchUsers(1);

  const handleToggleActive = async (userId: number) => {
    try {
      await adminUsersAPI.toggleActive(userId);
      message.success(t('adminPanel.updateSuccess'));
      fetchUsers(pagination.page);
    } catch {
      message.error(t('adminPanel.updateFailed'));
    }
  };

  const handleEdit = (user: User) => {
    setEditingUser(user);
    form.setFieldsValue({ username: user.username, role: user.role });
    setEditModalVisible(true);
  };

  const handleEditSubmit = async () => {
    if (!editingUser) return;
    try {
      const values = await form.validateFields();
      await adminUsersAPI.updateUser(editingUser.id, values);
      message.success(t('adminPanel.updateSuccess'));
      setEditModalVisible(false);
      fetchUsers(pagination.page);
    } catch {
      message.error(t('adminPanel.updateFailed'));
    }
  };

  const handleViewDetail = async (user: User) => {
    try {
      const res = await adminUsersAPI.getUserById(user.id);
      setDetailDrawer({ visible: true, user: res.data.user, stats: res.data.stats });
    } catch {
      message.error(t('adminPanel.fetchFailed'));
    }
  };

  const columns = [
    {
      title: 'ID',
      dataIndex: 'id',
      width: 60,
    },
    {
      title: t('adminPanel.colUsername'),
      dataIndex: 'username',
      render: (name: string, record: User) => (
        <Space>
          <Avatar icon={<UserOutlined />} src={record.avatar} size="small" />
          <Text>{name}</Text>
        </Space>
      ),
    },
    {
      title: t('adminPanel.colEmail'),
      dataIndex: 'email',
      render: (v: string) => v || '-',
    },
    {
      title: t('adminPanel.colPhone'),
      dataIndex: 'phone',
      render: (v: string) => v || '-',
    },
    {
      title: t('adminPanel.colAuthProvider'),
      dataIndex: 'auth_provider',
      render: (v: string) => {
        const colorMap: Record<string, string> = { local: 'blue', phone: 'green', google: 'red' };
        return <Tag color={colorMap[v] || 'default'}>{v || 'local'}</Tag>;
      },
    },
    {
      title: t('adminPanel.colRole'),
      dataIndex: 'role',
      render: (role: string) => (
        <Tag color={role === 'admin' ? 'gold' : 'default'}>{role}</Tag>
      ),
    },
    {
      title: t('adminPanel.colStatus'),
      dataIndex: 'is_active',
      render: (active: boolean) => (
        <Badge status={active ? 'success' : 'error'} text={active ? t('adminPanel.active') : t('adminPanel.disabled')} />
      ),
    },
    {
      title: t('adminPanel.colLastLogin'),
      dataIndex: 'last_login',
      render: (v: string) => v ? new Date(v).toLocaleDateString() : '-',
    },
    {
      title: t('adminPanel.colCreatedAt'),
      dataIndex: 'created_at',
      render: (v: string) => new Date(v).toLocaleDateString(),
    },
    {
      title: t('admin.colActions'),
      key: 'actions',
      width: 200,
      render: (_: any, record: User) => (
        <Space size="small">
          <Button size="small" icon={<EyeOutlined />} onClick={() => handleViewDetail(record)} />
          <Button size="small" icon={<EditOutlined />} onClick={() => handleEdit(record)} />
          <Popconfirm
            title={record.is_active ? t('adminPanel.confirmDisable') : t('adminPanel.confirmEnable')}
            onConfirm={() => handleToggleActive(record.id)}
          >
            <Button
              size="small"
              danger={record.is_active}
              icon={record.is_active ? <StopOutlined /> : <CheckCircleOutlined />}
            />
          </Popconfirm>
        </Space>
      ),
    },
  ];

  return (
    <div>
      <Title level={3} style={{ marginBottom: 24 }}>{t('adminPanel.userManagement')}</Title>

      <Card style={{ borderRadius: 12, marginBottom: 16 }} bodyStyle={{ padding: '16px 24px' }}>
        <Space wrap>
          <Input
            placeholder={t('adminPanel.searchUsers')}
            prefix={<SearchOutlined />}
            value={search}
            onChange={e => setSearch(e.target.value)}
            onPressEnter={handleSearch}
            style={{ width: 240 }}
            allowClear
          />
          <Select
            placeholder={t('adminPanel.colRole')}
            value={roleFilter}
            onChange={v => setRoleFilter(v)}
            allowClear
            style={{ width: 120 }}
            options={[
              { label: 'admin', value: 'admin' },
              { label: 'user', value: 'user' },
            ]}
          />
          <Select
            placeholder={t('adminPanel.colStatus')}
            value={activeFilter}
            onChange={v => setActiveFilter(v)}
            allowClear
            style={{ width: 120 }}
            options={[
              { label: t('adminPanel.active'), value: 'true' },
              { label: t('adminPanel.disabled'), value: 'false' },
            ]}
          />
          <Button type="primary" icon={<SearchOutlined />} onClick={handleSearch}>
            {t('common.search')}
          </Button>
          <Button icon={<ReloadOutlined />} onClick={() => { setSearch(''); setRoleFilter(undefined); setActiveFilter(undefined); fetchUsers(1); }}>
            {t('tools.resetFilter')}
          </Button>
        </Space>
      </Card>

      <Card style={{ borderRadius: 12 }}>
        <Table
          loading={loading}
          dataSource={users}
          columns={columns}
          rowKey="id"
          scroll={{ x: 1000 }}
          pagination={{
            current: pagination.page,
            pageSize: pagination.limit,
            total: pagination.total,
            showTotal: total => t('admin.totalItems').replace('{total}', String(total)),
            onChange: page => fetchUsers(page),
          }}
        />
      </Card>

      {/* Edit Modal */}
      <Modal
        title={t('adminPanel.editUser')}
        open={editModalVisible}
        onOk={handleEditSubmit}
        onCancel={() => setEditModalVisible(false)}
        okText={t('admin.save')}
        cancelText={t('admin.cancel')}
      >
        <Form form={form} layout="vertical">
          <Form.Item label={t('adminPanel.colUsername')} name="username" rules={[{ required: true }]}>
            <Input />
          </Form.Item>
          <Form.Item label={t('adminPanel.colRole')} name="role" rules={[{ required: true }]}>
            <Select
              options={[
                { label: 'admin', value: 'admin' },
                { label: 'user', value: 'user' },
              ]}
            />
          </Form.Item>
        </Form>
      </Modal>

      {/* Detail Drawer */}
      <Drawer
        title={t('adminPanel.userDetail')}
        open={detailDrawer.visible}
        onClose={() => setDetailDrawer({ visible: false })}
        width={480}
      >
        {detailDrawer.user && (
          <Descriptions column={1} bordered size="small">
            <Descriptions.Item label="ID">{detailDrawer.user.id}</Descriptions.Item>
            <Descriptions.Item label={t('adminPanel.colUsername')}>{detailDrawer.user.username}</Descriptions.Item>
            <Descriptions.Item label={t('adminPanel.colEmail')}>{detailDrawer.user.email || '-'}</Descriptions.Item>
            <Descriptions.Item label={t('adminPanel.colPhone')}>{detailDrawer.user.phone || '-'}</Descriptions.Item>
            <Descriptions.Item label={t('adminPanel.colAuthProvider')}>{detailDrawer.user.auth_provider || 'local'}</Descriptions.Item>
            <Descriptions.Item label={t('adminPanel.colRole')}>
              <Tag color={detailDrawer.user.role === 'admin' ? 'gold' : 'blue'}>{detailDrawer.user.role}</Tag>
            </Descriptions.Item>
            <Descriptions.Item label={t('adminPanel.colStatus')}>
              <Badge
                status={detailDrawer.user.is_active ? 'success' : 'error'}
                text={detailDrawer.user.is_active ? t('adminPanel.active') : t('adminPanel.disabled')}
              />
            </Descriptions.Item>
            <Descriptions.Item label={t('adminPanel.colLastLogin')}>
              {detailDrawer.user.last_login ? new Date(detailDrawer.user.last_login).toLocaleString() : '-'}
            </Descriptions.Item>
            <Descriptions.Item label={t('adminPanel.colCreatedAt')}>
              {new Date(detailDrawer.user.created_at).toLocaleString()}
            </Descriptions.Item>
            {detailDrawer.stats && (
              <>
                <Descriptions.Item label={t('adminPanel.totalReviews')}>
                  {detailDrawer.stats.reviewCount}
                </Descriptions.Item>
                <Descriptions.Item label={t('adminPanel.totalFavorites')}>
                  {detailDrawer.stats.favoriteCount}
                </Descriptions.Item>
              </>
            )}
          </Descriptions>
        )}
      </Drawer>
    </div>
  );
};

export default AdminUserManagementPage;
